import { useState, useEffect, createContext, useContext } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const AccessContext = createContext({ modules: null, loading: true });

const SUPPORTED_ROLES = ['teacher', 'student', 'finance', 'coordinator', 'admin', 'librarian', 'hostel_member'];

export const AccessProvider = ({ children }) => {
  const { user } = useAuth();
  const [modules, setModules] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const role = user?.role;
    if (!role || !SUPPORTED_ROLES.includes(role)) { setLoading(false); return; }

    // Owner impersonating a branch admin sees everything, unrestricted —
    // the admin's own module restrictions never apply to the owner wearing them.
    if (role === 'admin' && user.impersonatedByRole === 'owner') {
      setModules(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const url = role === 'admin' ? '/admin/access/my-access' : `/${role}/access`;
    api.get(url)
      .then(r => setModules(r.data.modules || []))
      .catch(() => setModules(null)) // null = full access on error
      .finally(() => setLoading(false));
  }, [user?._id, user?.role, user?.impersonatedByRole]);

  return (
    <AccessContext.Provider value={{ modules, loading }}>
      {children}
    </AccessContext.Provider>
  );
};

// Returns true if the module is accessible
export const useAccess = (moduleKey) => {
  const { modules, loading } = useContext(AccessContext);
  if (loading) return { allowed: true,  loading: true };
  if (modules === null) return { allowed: true, loading: false }; // error = allow
  return { allowed: modules.includes(moduleKey), loading: false };
};

export const useAllModules = () => useContext(AccessContext);
